/**
 * Idempotent refresh (Lot B, "solution C").
 *
 * When a refresh answer is lost (phone suspended, tunnel, flaky network) the
 * server has already rotated the refresh token but the browser still holds the
 * old one. The client therefore sends a random ATTEMPT ID with every refresh
 * and re-sends the SAME id until it has seen an answer; the server then hands
 * back the very same successor instead of treating the old token as stolen.
 *
 * Only the attempt id and two timestamps are kept in localStorage. They are not
 * credentials: useless without the HttpOnly refresh cookie. No token ever
 * touches web storage.
 */
export const ATTEMPT_HEADER = 'X-Ridalo-Refresh-Attempt';
const ATTEMPT_KEY = 'ridalo:refreshAttempt';
const OK_KEY = 'ridalo:refreshOkAt';
// Server window is 10 minutes: stay safely inside it.
export const ATTEMPT_MAX_AGE_MS = 9 * 60 * 1000;
// A refresh that another tab completed this recently makes ours pointless.
export const RECENT_REFRESH_MS = 5 * 1000;

const read = (key) => { try { return window.localStorage.getItem(key); } catch { return null; } };
const write = (key, value) => { try { window.localStorage.setItem(key, value); } catch { /* private mode */ } };
const remove = (key) => { try { window.localStorage.removeItem(key); } catch { /* private mode */ } };

const randomId = () => {
  const c = typeof window !== 'undefined' ? window.crypto : null;
  if (!c || !c.getRandomValues) return null;          // no secure randomness: plain refresh, no recovery
  const bytes = c.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** The unfinished attempt's id (reused), or a fresh one. */
export const getAttemptId = (now = Date.now()) => {
  try {
    const saved = JSON.parse(read(ATTEMPT_KEY) || 'null');
    if (saved && typeof saved.id === 'string' && now - saved.at < ATTEMPT_MAX_AGE_MS) return saved.id;
  } catch { /* corrupt value: replaced below */ }
  const id = randomId();
  if (id) write(ATTEMPT_KEY, JSON.stringify({ id, at: now }));
  return id;
};

/** The answer was seen (success or refusal): the attempt is over. */
export const clearAttemptId = () => remove(ATTEMPT_KEY);

export const markRefreshOk = (now = Date.now()) => write(OK_KEY, String(now));
export const refreshedRecently = (now = Date.now()) => {
  const at = Number(read(OK_KEY));
  return Number.isFinite(at) && at > 0 && now - at >= 0 && now - at < RECENT_REFRESH_MS;
};
